/**
 * Game constants. Every number in here is normative — SPEC.md is the source.
 * Servers and clients must both read from this module rather than inlining values.
 */

/** SPEC §2.1 — below 4 players, top/bottom prediction is a coin flip. */
export const MIN_PLAYERS = 4;
/** SPEC §2.1 / §7 — player counts above 12 are explicitly deferred. */
export const MAX_PLAYERS = 12;

/** SPEC §2.2 — host sets number of rounds. */
export const MIN_ROUNDS = 1;
export const MAX_ROUNDS = 10;
export const DEFAULT_ROUNDS = 2;

/** SPEC §2.2 — lobby time estimate, ~2.5 min per turn. */
export const ESTIMATED_MS_PER_TURN = 150_000;

/** SPEC §2.3 phase 3 — the guillotine. */
export const LOCK_COUNTDOWN_MS = 10_000;

/** SPEC §5.4 — grace before the host is offered "Skip [name]'s turn". */
export const RECONNECT_GRACE_MS = 30_000;

/** SPEC §6 — rooms self-purge after 4 hours idle. */
export const ROOM_IDLE_TTL_MS = 4 * 60 * 60 * 1000;

/** SPEC §2.1 — first name only. */
export const MAX_NAME_LENGTH = 12;
export const MIN_NAME_LENGTH = 1;

/**
 * SPEC §5.1 — 4-letter room code, ambiguous glyphs (I/1/O/0) excluded.
 * Letters only, so there is no 1/l or 0/O confusion at all.
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 4;

/**
 * The numbered grid is a promise: every number the grid shows must resolve to a
 * real question, so a deck ships exactly `deckSizeFor(deckId)` of them, numbered
 * from 1 with no gaps. v1 shipped one deck of 100; the standard deck is now the
 * full 201-question list in QUESTIONS.md.
 *
 * `QUESTION_NUMBER_MAX` is the widest grid any deck may present — the bound the
 * wire schema and the D1 CHECK constraint are written against. A given room's
 * grid is the size of *its* deck, which is smaller in safe mode.
 */
export const QUESTION_NUMBER_MIN = 1;
export const QUESTION_NUMBER_MAX = 201;
/** The standard deck's size. Prefer `deckSizeFor(settings.deckId)` in room code. */
export const DECK_SIZE = 201;

/** SPEC §3.1 — `deck_id` exists from day one; the deck picker is `safeMode`. */
export const DEFAULT_DECK_ID = 'standard';

/**
 * SPEC §3.1's Work-Safe deck, surfaced to the host as **Safe mode**: the
 * standard deck with everything above `SAFE_MAX_SPICE` removed — no sex, no
 * drugs, nothing explicit — and the survivors renumbered 1..`SAFE_DECK_SIZE`
 * in their original order.
 *
 * Renumbered, rather than the standard deck with holes in it, because the
 * numbered grid is a board and not a filter: a room in safe mode scrolls
 * eighty-eight live numbers, not two hundred and one of which most are dead.
 * The cost is that a number means a different question in each deck, which is
 * why changing decks wipes the room's burned numbers (`update_settings`).
 */
export const SAFE_DECK_ID = 'safe';
export const SAFE_DECK_SIZE = 88;

/**
 * The spice ceiling for the safe deck. 0-1 is "personal and mean, but nothing
 * you'd have to apologise for" (see the scale in `content/src/index.ts`); 2 and
 * 3 are the ones that aim at a body, a partner, a substance or a bed.
 */
export const SAFE_MAX_SPICE = 1;

/** Every deck that ships, in the order a picker would show them. */
export const DECK_IDS = [DEFAULT_DECK_ID, SAFE_DECK_ID] as const;
export type DeckId = (typeof DECK_IDS)[number];

const DECK_SIZES: Record<string, number> = {
  [DEFAULT_DECK_ID]: DECK_SIZE,
  [SAFE_DECK_ID]: SAFE_DECK_SIZE,
};

export function isKnownDeckId(deckId: string): deckId is DeckId {
  return deckId in DECK_SIZES;
}

/**
 * How many numbers a deck's grid shows. Both sides read this rather than
 * `DECK_SIZE`: the client to draw the board, the server to work out how many
 * rounds the remaining numbers can pay for. An unknown deck id falls back to
 * the standard size, which is what a room loaded from storage under an older
 * build would carry.
 */
export function deckSizeFor(deckId: string): number {
  return DECK_SIZES[deckId] ?? DECK_SIZE;
}

/** True when the room is playing the family-friendly deck. */
export function isSafeMode(deckId: string): boolean {
  return deckId === SAFE_DECK_ID;
}

/** SPEC §2.4 — points = BASE + STEP × (round − 1). */
export const POINTS_BASE = 100;
export const POINTS_ROUND_STEP = 50;
/** SPEC §2.4 — both top and bottom correct multiplies the turn's total. */
export const PERFECT_READ_MULTIPLIER = 1.5;

/** SPEC §6 — client-side photo budget before upload. */
export const PHOTO_MAX_WIDTH = 400;
export const PHOTO_MAX_HEIGHT = 500;
export const PHOTO_MAX_BYTES = 80_000;
export const PHOTO_MIME = 'image/webp';

/* -------------------------------------------------------------------------- */
/* SPEC §2.3 phase 4 / §5.2 — the reveal's clock                               */
/* -------------------------------------------------------------------------- */

/**
 * The reveal's pacing, in ms after the server's `reveal.startAt`.
 *
 * This lives in the protocol because **two independent processes have to agree
 * on it**. The client animates the sequence; the server schedules the *hard
 * cut* to justification (SPEC §5.2) off the same curve. A server timer shorter
 * than the animation severs the attribution beat mid-sentence; a longer one
 * turns the hard cut into dead air. It used to be declared twice — once in
 * `client/src/components/reveal/timeline.ts` and once in
 * `worker/src/roomState.ts` — with a comment on each asking the other to keep
 * up. Both now derive from here, so there is nothing left to drift.
 */
export const REVEAL_TIMELINE = {
  /** Question restated, "here's the answer". */
  introMs: 0,
  /** 1st place. */
  topMs: 700,
  /** Last place. */
  bottomMs: 2_100,
  /** The middle starts cascading. */
  middleMs: 3_400,
  /** Per-row stagger through the middle. */
  middleStaggerMs: 110,
  /** Beat between the last middle row landing and the receipts coming out. */
  attributionGapMs: 400,
  /** Per-row stagger as attribution lines appear. */
  attributionStaggerMs: 70,
  /** How long the attribution has the screen to itself before the score. */
  attributionDwellMs: 1_800,
  /** How long the delta sits before the hard cut lands. */
  deltaDwellMs: 1_600,
} as const;

/**
 * Total wall time of the reveal for a ranking of `n`, from `startAt` to the
 * moment the hard cut should land.
 *
 *   duration(n) = 7200ms + (n − 2) × 110ms   → 7420ms at 4, 8300ms at 12
 */
export function revealDurationMs(n: number): number {
  const middleCount = Math.max(0, n - 2);
  return (
    REVEAL_TIMELINE.middleMs +
    middleCount * REVEAL_TIMELINE.middleStaggerMs +
    REVEAL_TIMELINE.attributionGapMs +
    REVEAL_TIMELINE.attributionDwellMs +
    REVEAL_TIMELINE.deltaDwellMs
  );
}

/** Websocket path the client connects to. Kept here so both sides agree. */
export const WS_PATH = '/ws';

/** localStorage key holding the `room + seat` session token (SPEC §5.4). */
export const SESSION_STORAGE_KEY = 'idgame.session';
